//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useRef, useState } from 'react';
import { useMove } from 'react-aria';

interface UseRangeDragProps {
  /** The current [min, max] values of the range slider. */
  value: number[];
  /** Called with the new [min, max] when the range is dragged. */
  onChange: (value: number[]) => void;
  /** The slider's minimum allowed value. */
  minValue: number;
  /** The slider's maximum allowed value. */
  maxValue: number;
  /** The slider's step increment. */
  step: number;
  /** Syncs react-aria's internal state so subsequent interactions read correct values. */
  setThumbValue: (index: number, value: number) => void;
}

interface UseRangeDragResult {
  /**
   * Props to spread onto `Slider.FillTrack`.
   * Includes pointer/touch handlers for dragging and accessibility attributes.
   */
  rangeDragProps: React.HTMLAttributes<HTMLDivElement>;
  /** Whether the range is currently being dragged. */
  isDragging: boolean;
}

const decimalPrecision = (step: number): number => {
  const str = step.toString();
  const dot = str.indexOf('.');
  return dot === -1 ? 0 : str.length - dot - 1;
};

const snapToStep = (val: number, step: number): number => {
  const precision = decimalPrecision(step);
  return parseFloat((Math.round(val / step) * step).toFixed(precision));
};

/**
 * Enables dragging the filled area of a multi-thumb slider to move both thumbs
 * simultaneously while preserving the span between them.
 *
 * This is a pointer convenience — the fill track is hidden from the
 * accessibility tree (`aria-hidden`), so keyboard and screen reader users
 * interact with the individual thumbs as normal.
 *
 * Must be used inside a `Slider` component so that `SliderStateContext` is
 * available for positioning. The `onChange` callback fires once per move with
 * both values updated atomically.
 *
 * @example
 * const { rangeDragProps } = useRangeDrag({
 *   value, onChange: setValue, minValue: 0, maxValue: 100, step: 1,
 * });
 *
 * <Slider.FillTrack
 *   {...rangeDragProps}
 *   maxPosition={state.getThumbPercent(1)}
 *   minPosition={state.getThumbPercent(0)}
 * />
 */
export const useRangeDrag = ({
  maxValue,
  minValue,
  onChange,
  setThumbValue,
  step,
  value,
}: UseRangeDragProps): UseRangeDragResult => {
  const [isDragging, setIsDragging] = useState(false);
  const trackWidthRef = useRef(0);

  // During a drag, locally computed values are authoritative — don't let re-renders overwrite them.
  const valueRef = useRef(value);
  if (!isDragging) {
    valueRef.current = value;
  }

  const { moveProps } = useMove({
    onMoveStart() {
      setIsDragging(true);
    },
    onMove(e) {
      const currentMin = valueRef.current[0];
      const currentMax = valueRef.current[1];
      if (currentMin === undefined || currentMax === undefined) {
        return;
      }

      const span = currentMax - currentMin;
      const delta = (() => {
        if (e.pointerType === 'keyboard') {
          if (e.deltaX > 0) {
            return step;
          }

          if (e.deltaX < 0) {
            return -step;
          }

          return 0;
        }

        if (trackWidthRef.current === 0) {
          return undefined;
        }

        return (e.deltaX / trackWidthRef.current) * (maxValue - minValue);
      })();

      if (delta === undefined) {
        return;
      }

      const snappedMin = snapToStep(currentMin + delta, step);
      const snappedMax = snapToStep(currentMax + delta, step);

      // Clamp to bounds while preserving span
      const clampedMin = Math.max(snappedMin, minValue);
      const clampedMax = Math.min(snappedMax, maxValue);
      const newMin = Math.max(
        minValue,
        clampedMax < snappedMax ? clampedMax - span : clampedMin,
      );
      const newMax = Math.min(
        maxValue,
        clampedMin > snappedMin ? clampedMin + span : clampedMax,
      );

      if (newMin === currentMin && newMax === currentMax) {
        return;
      }

      valueRef.current = [newMin, newMax];

      // Sync react-aria's internal state so subsequent thumb interactions
      // (keyboard/mouse) read the correct values for both thumbs.
      setThumbValue(0, newMin);
      setThumbValue(1, newMax);

      onChange([newMin, newMax]);
    },
    onMoveEnd() {
      setIsDragging(false);
    },
  });

  const rangeDragProps: React.HTMLAttributes<HTMLDivElement> = {
    ...moveProps,
    // Range dragging is a pointer convenience — the individual thumbs remain the
    // accessible controls. Hiding the fill track from the accessibility tree
    // prevents it from appearing as an unlabeled focusable element.
    'aria-hidden': true,
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => {
      // Prevent the track's click-to-seek behavior
      e.stopPropagation();
      // Capture track width for pixel-to-value conversion
      const trackEl = e.currentTarget.parentElement;
      if (trackEl) {
        trackWidthRef.current = trackEl.getBoundingClientRect().width;
      }
      // Forward to useMove's handler
      moveProps.onPointerDown?.(e);
    },
    style: {
      cursor: isDragging ? 'grabbing' : 'grab',
      touchAction: 'none',
    },
  };

  return { rangeDragProps, isDragging };
};
